// Enhanced Performance Optimizations for Red Helicopter Platform

import { lazy } from 'react';

// 1. Route-based code splitting with prefetch
export const prefetchRoute = (routeName: string) => {
  switch (routeName) {
    case 'journey':
      import('@/pages/RedHelicopterJourneyPage');
      break;
    case 'achievements':
      import('@/pages/EnhancedAchievementsPage');
      break;
    case 'profile':
      import('@/pages/ProfilePage');
      break;
    // Add more routes as needed
  }
};

// 2. Image optimization with responsive sizes
export const getOptimizedImageSrc = (
  src: string,
  width: number,
  format: 'webp' | 'jpg' | 'png' = 'webp'
): string => {
  // In production, this would integrate with an image CDN
  // For now, return original src
  return src;
};

// 3. API response caching strategy
export const cacheConfig = {
  user: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  },
  journey: {
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
  },
  static: {
    staleTime: 60 * 60 * 1000, // 1 hour
    cacheTime: 24 * 60 * 60 * 1000, // 24 hours
  }
};

// 4. Service Worker registration for offline support
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  }
};

// 5. Web Vitals monitoring
export const reportWebVitals = (metric: any) => {
  // Send to analytics endpoint
  if (process.env.NODE_ENV === 'production') {
    const body = JSON.stringify({
      name: metric.name,
      value: metric.value,
      delta: metric.delta,
      id: metric.id,
    });
    
    // Use sendBeacon for reliability
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/analytics/vitals', body);
    }
  }
};

// 6. Resource hints for critical resources
export const addResourceHints = () => {
  const criticalResources = [
    { url: '/api/auth/user', as: 'fetch' },
    { url: '/api/journey/progress', as: 'fetch' },
  ];

  criticalResources.forEach(({ url, as }) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = as;
    link.href = url;
    document.head.appendChild(link);
  });
};

// 7. Optimize React renders with memo comparison
export const arePropsEqual = (prevProps: any, nextProps: any): boolean => {
  const keys = Object.keys(prevProps);
  for (const key of keys) {
    if (prevProps[key] !== nextProps[key]) {
      return false;
    }
  }
  return true;
};

// 8. Virtual scrolling helper for long lists
export const calculateVisibleItems = <T,>(
  items: T[],
  scrollTop: number,
  containerHeight: number,
  itemHeight: number,
  buffer = 5
): { visibleItems: T[], startIndex: number, endIndex: number } => {
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - buffer);
  const endIndex = Math.min(
    items.length,
    Math.ceil((scrollTop + containerHeight) / itemHeight) + buffer
  );
  
  return {
    visibleItems: items.slice(startIndex, endIndex),
    startIndex,
    endIndex
  };
};

// 9. Network status monitoring
export const useNetworkStatus = () => {
  if (typeof navigator !== 'undefined' && 'connection' in navigator) {
    const connection = (navigator as any).connection;
    return {
      effectiveType: connection?.effectiveType || '4g',
      saveData: connection?.saveData || false,
      rtt: connection?.rtt || 0,
    };
  }
  return { effectiveType: '4g', saveData: false, rtt: 0 };
};

// 10. Batch DOM updates
export class DOMBatcher {
  private updates: Array<() => void> = [];
  private scheduled = false;

  add(update: () => void) {
    this.updates.push(update);
    if (!this.scheduled) {
      this.scheduled = true;
      requestAnimationFrame(() => this.flush());
    }
  }

  flush() {
    const updates = this.updates.slice();
    this.updates = [];
    this.scheduled = false;
    updates.forEach(update => update());
  }
}

// 11. Preconnect to external domains
export const preconnectDomains = [
  'https://fonts.googleapis.com',
  'https://storage.googleapis.com',
];

export const addPreconnects = () => {
  preconnectDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};

// 12. Memory leak prevention
export const createCleanupManager = () => {
  const cleanups = new Set<() => void>();
  
  return {
    add: (cleanup: () => void) => cleanups.add(cleanup),
    remove: (cleanup: () => void) => cleanups.delete(cleanup),
    cleanup: () => {
      cleanups.forEach(fn => fn());
      cleanups.clear();
    }
  };
};

// 13. Optimize animations for performance
export const shouldReduceMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const getAnimationDuration = (baseDuration: number): number => {
  return shouldReduceMotion() ? 0 : baseDuration;
};

// 14. Critical CSS extraction hint
export const criticalStyles = `
  /* Add critical inline styles here for faster FCP */
  .cosmic-bg { background: linear-gradient(to bottom, #1a1a2e, #0a0a0f); }
  .loading-spinner { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// 15. Performance budget monitoring
export const performanceBudget = {
  js: 300 * 1024, // 300KB
  css: 100 * 1024, // 100KB
  images: 500 * 1024, // 500KB per image
  totalPageWeight: 2 * 1024 * 1024, // 2MB
  fcp: 2000, // First Contentful Paint: 2s
  lcp: 3000, // Largest Contentful Paint: 3s
  fid: 100, // First Input Delay: 100ms
  cls: 0.1, // Cumulative Layout Shift: 0.1
};